import Phaser from 'phaser';

/**
 * FogManager
 * Manages fog sprites that appear outside the safe zone boundary
 * Creates an atmospheric fog effect that intensifies as the zone shrinks
 */
export class FogManager {
  constructor(scene, battlefieldSize = 'small', objectScale = 1.0) {
    this.scene = scene;
    this.objectScale = objectScale;
    this.activeFogSprites = [];
    
    // Determine battlefield dimensions (don't multiply by objectScale - these are world coordinates)
    const isLarge = battlefieldSize === 'large' || battlefieldSize === 'largew';
    this.battlefieldWidth = battlefieldSize === 'largew' ? 4906 : (isLarge ? 4096 : 2048);
    this.battlefieldHeight = battlefieldSize === 'largew' ? 2304 : (isLarge ? 4096 : 2048);
    
    // Fog spawn configuration
    this.spawnTimer = 0;
    this.spawnInterval = 30; // Much denser spawning (~25 per second)
    this.isActive = false;
    
    console.log(`[FogManager] Initialized for battlefield ${this.battlefieldWidth}x${this.battlefieldHeight}`);
  }
  
  /**
   * Start the fog system
   */
  start() {
    this.isActive = true;
    console.log(`[FogManager] Fog system started`);
  }
  
  /**
   * Stop the fog system and clean up
   */
  stop() {
    this.isActive = false;
    this.cleanup();
    console.log(`[FogManager] Fog system stopped`);
  }
  
  /**
   * Spawn a single fog sprite outside the zone boundary
   */
  spawnFog(zoneBounds) {
    if (!zoneBounds) return;

    // Build spawn bands outside the zone on all four sides.
    // Extend beyond world edges slightly so we can spawn just offscreen as well.
    const extra = 200; // extend outside world a bit
    const margin = -40; // gap from the zone edge to avoid overlap

    const bands = [];
    const zoneRight = zoneBounds.x + zoneBounds.width;
    const zoneBottom = zoneBounds.y + zoneBounds.height;

    // Top band
    const topMinY = Math.min(-extra, zoneBounds.y - extra);
    const topMaxY = zoneBounds.y - margin;
    if (topMaxY > topMinY) {
      bands.push({ minX: -extra, maxX: this.battlefieldWidth + extra, minY: topMinY, maxY: topMaxY });
    }

    // Bottom band
    const botMinY = zoneBottom + margin;
    const botMaxY = this.battlefieldHeight + extra;
    if (botMaxY > botMinY) {
      bands.push({ minX: -extra, maxX: this.battlefieldWidth + extra, minY: botMinY, maxY: botMaxY });
    }

    // Left band
    const leftMinX = Math.min(-extra, zoneBounds.x - extra);
    const leftMaxX = zoneBounds.x - margin;
    if (leftMaxX > leftMinX) {
      bands.push({ minX: leftMinX, maxX: leftMaxX, minY: -extra, maxY: this.battlefieldHeight + extra });
    }

    // Right band
    const rightMinX = zoneRight + margin;
    const rightMaxX = this.battlefieldWidth + extra;
    if (rightMaxX > rightMinX) {
      bands.push({ minX: rightMinX, maxX: rightMaxX, minY: -extra, maxY: this.battlefieldHeight + extra });
    }

    if (bands.length === 0) return; // no space outside the zone

    // Pick a random band (uniformly) and spawn within it
    const band = bands[Phaser.Math.Between(0, bands.length - 1)];
    const x = Phaser.Math.Between(Math.floor(band.minX), Math.floor(band.maxX));
    const y = Phaser.Math.Between(Math.floor(band.minY), Math.floor(band.maxY));
    
    // Random scale variation (~4x larger than previous)
    const scale = Phaser.Math.FloatBetween(4.0, 5.0) * Math.min(this.objectScale, 1.0);
    
    // Random rotation
    const rotation = Phaser.Math.FloatBetween(0, Math.PI * 2);
    
    // Random opacity (keep moderate for layering many sprites)
    const targetOpacity = Phaser.Math.FloatBetween(0.25, 0.3);
    
    // Create fog sprite
    const fogSprite = this.scene.add.sprite(x, y, 'fog1');
    fogSprite.setDepth(14); // Below clouds (15) but above most game objects
    fogSprite.setScale(scale);
    fogSprite.setRotation(rotation);
    fogSprite.setAlpha(0); // Start invisible
    // Don't tint - texture already has good transparency/shading built in
    fogSprite.setBlendMode(Phaser.BlendModes.NORMAL);
    
    // Store fog data for lifecycle management
    fogSprite.fogData = {
      spawnTime: this.scene.time.now,
      lifetime: 8000, // 3 seconds total
      fadeInDuration: 500, // 0.5s fade in
      fadeOutDuration: 3000, // 0.5s fade out
      targetOpacity: targetOpacity,
      phase: 'fadein' // 'fadein', 'visible', 'fadeout'
    };
    
    this.activeFogSprites.push(fogSprite);
    
    // UI camera should ignore fog
    if (this.scene.uiCamera) {
      this.scene.uiCamera.ignore(fogSprite);
    }
    
    //console.log(`[FogManager] Spawned fog at (${Math.round(x)}, ${Math.round(y)}) outside zone. Total fog: ${this.activeFogSprites.length}`);
  }
  
  /**
   * Update all fog sprites - handle fade in/out and cleanup
   */
  update(delta, zoneBounds) {
    if (!this.isActive || !zoneBounds) return;
    
    const now = this.scene.time.now;
    
    // Spawn new fog sprites periodically (catch up if we lagged)
    this.spawnTimer += delta;
    while (this.spawnTimer >= this.spawnInterval) {
      // Spawn multiple per tick to create denser coverage
      this.spawnFog(zoneBounds);
      if (Math.random() < 0.6) this.spawnFog(zoneBounds);
      if (Math.random() < 0.3) this.spawnFog(zoneBounds);
      this.spawnTimer -= this.spawnInterval;
    }
    
    // Update existing fog sprites
    for (let i = this.activeFogSprites.length - 1; i >= 0; i--) {
      const fogSprite = this.activeFogSprites[i];
      const data = fogSprite.fogData;
      const elapsed = now - data.spawnTime;
      
      // Phase management
      if (data.phase === 'fadein') {
        // Fade in phase
        const fadeProgress = Math.min(1, elapsed / data.fadeInDuration);
        fogSprite.setAlpha(fadeProgress * data.targetOpacity);
        
        if (elapsed >= data.fadeInDuration) {
          data.phase = 'visible';
        }
      } else if (data.phase === 'visible') {
        // Visible phase
        const visibleDuration = data.lifetime - data.fadeInDuration - data.fadeOutDuration;
        if (elapsed >= data.fadeInDuration + visibleDuration) {
          data.phase = 'fadeout';
          data.fadeOutStart = now;
        }
      } else if (data.phase === 'fadeout') {
        // Fade out phase
        const fadeOutElapsed = now - data.fadeOutStart;
        const fadeProgress = Math.min(1, fadeOutElapsed / data.fadeOutDuration);
        fogSprite.setAlpha(data.targetOpacity * (1 - fadeProgress));
        
        if (fadeOutElapsed >= data.fadeOutDuration) {
          // Remove and destroy
          fogSprite.destroy();
          this.activeFogSprites.splice(i, 1);
        }
      }
      
      // Safety check - destroy if somehow alive past lifetime
      if (elapsed >= data.lifetime + 500) {
        fogSprite.destroy();
        this.activeFogSprites.splice(i, 1);
      }
    }
  }
  
  /**
   * Clean up all fog sprites
   */
  cleanup() {
    if (this.activeFogSprites) {
      this.activeFogSprites.forEach(fog => {
        if (fog && fog.destroy) {
          fog.destroy();
        }
      });
      this.activeFogSprites = [];
    }
  }
  
  /**
   * Destroy the fog manager
   */
  destroy() {
    this.stop();
    this.cleanup();
  }
}

export default FogManager;

